import React, { useEffect, useState } from 'react';
import { Layout, Button, Avatar, Dropdown, Menu, Spin } from 'antd';
import { ProfileOutlined, LogoutOutlined } from '@ant-design/icons';
import { Route, Routes, useNavigate, useLocation } from 'react-router-dom';
import NurseOnboarding from './Onboarding';
import logo from '../../assets/images/bg/logo.svg';
import { useAuth } from '../../AuthProvider';
import { getNurseProfile, listNurseDocuments } from '../../api';
import AppDownloadHero from '../../components/AppDownloadHero';

const { Header, Content } = Layout;

const DOCUMENT_DEFINITIONS = [
  { docType: "Resume", category: "application", onboarding: true },
  { docType: "Photo", category: "application", onboarding: false },
  { docType: "ID", category: "compliance", onboarding: false },
  { docType: "RN License", category: "compliance", onboarding: false },
  { docType: "RN Registration", category: "compliance", onboarding: false },
  { docType: "BLS", category: "compliance", onboarding: false },
  { docType: "ACLS", category: "compliance", onboarding: false },
  { docType: "PALS", category: "compliance", onboarding: false },
  { docType: "NRP", category: "compliance", onboarding: false },
  { docType: "NIHSS", category: "compliance", onboarding: false },
  { docType: "TNCC", category: "compliance", onboarding: false },
  { docType: "Infection Control", category: "compliance", onboarding: false },
  { docType: "Skills Checklist", category: "compliance", onboarding: false }
];

const NurseHome = () => {
  const [profile, setProfile] = useState({ fullName: 'Healthcare Professional', email: '', units: [] });
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const load = async () => {
      try {
        const [profileRes, docsRes] = await Promise.all([
          getNurseProfile().catch(() => ({ data: {} })),
          listNurseDocuments().catch(() => ({ data: [] }))
        ]);
        
        const data = profileRes?.data || {};
        const docs = docsRes?.data || [];
        
        // Check if onboarding is completed
        const isPersonalDone = !!(data.firstName && data.lastName && data.dob);
        const isContactDone = !!data.phone;
        const isAddressDone = !!(data.address && data.address.street && data.address.city && data.address.state && data.address.zipCode && data.address.country);
        const isUnitsDone = Array.isArray(data.units) && data.units.length > 0;
        
        const requiredDocTypes = DOCUMENT_DEFINITIONS.filter(d => d.onboarding).map(d => d.docType);
        const numRequiredUploaded = requiredDocTypes.filter(t => docs.some(d => d.docType === t)).length;
        const isDocumentsDone = numRequiredUploaded === requiredDocTypes.length;
        
        if (!isPersonalDone || !isContactDone || !isAddressDone || !isUnitsDone || !isDocumentsDone) {
          navigate('/employee/onboarding', { replace: true });
          return;
        }

        setProfile({
          fullName: data.fullName || 'Healthcare Professional',
          email: data.email || '',
          units: Array.isArray(data.units) ? data.units : [],
        });
      } catch (_) {
        // best-effort only
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [navigate]);

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 'calc(100dvh - 64px)' }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <AppDownloadHero
      embedded
      welcomeName={profile.fullName}
      showProfileButton
      onProfileClick={() => navigate('/employee/onboarding')}
    />
  );
};

const NurseDashboard = () => {
  const { currentUser, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const isHome = location.pathname === '/employee' || location.pathname === '/employee/';

  const userMenu = (
    <Menu style={{ minWidth: 200, borderRadius: 8 }}>
      <Menu.Item key="edit" icon={<ProfileOutlined />} onClick={() => navigate('/employee/onboarding')}>
        Edit Profile
      </Menu.Item>
      <Menu.Divider />
      <Menu.Item key="logout" icon={<LogoutOutlined />} onClick={logout}>
        Logout
      </Menu.Item>
    </Menu>
  );

  return (
    <Layout style={{ minHeight: '100dvh', background: isHome ? '#006971' : '#f5fbfb' }}>
      <Header
        style={{
          background: 'rgba(255, 255, 255, 0.96)',
          borderBottom: '1px solid rgba(0, 0, 0, 0.06)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '0 20px',
          height: 64,
          lineHeight: '64px',
          position: 'sticky',
          top: 0,
          zIndex: 10,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <img src={logo} alt="VitalStaf" style={{ height: 28 }} />
        </div>
        <Dropdown overlay={userMenu} trigger={['click']}>
          <Button type="text" style={{ padding: '0 4px', height: 'auto' }}>
            <Avatar style={{ backgroundColor: '#006971', marginRight: 8, color: '#fff' }}>
              {currentUser?.displayName?.[0] || currentUser?.email?.[0] || 'U'}
            </Avatar>
            <span style={{ maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {currentUser?.displayName || currentUser?.email}
            </span>
          </Button>
        </Dropdown>
      </Header>
      <Content style={{ padding: 0, background: 'transparent', flex: 1 }}>
        <Routes>
          <Route path="/" element={<NurseHome />} />
          <Route path="onboarding" element={<NurseOnboarding />} />
        </Routes>
      </Content>
    </Layout>
  );
};

export default NurseDashboard;


